import { useState, useMemo, Fragment, useEffect, useRef } from 'react';
import StandardPage from '../../components/StandardPage';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { useOrganization } from '../../context/organization';
import { Dialog, Transition } from '@headlessui/react';
import { handleApiError } from '../../api-client/error_handler';
import axios from 'axios';
import {
  trackCreditsAddClick,
  trackCreditsCheckoutStart,
  trackCreditsModalClose,
  trackCreditsQuickPick,
} from '../../analytics';

const MIN_EUR = 5;
const MAX_EUR = 1000;
const STEP = 5;
const DEFAULT_CREDITS_PER_EUR = 100000;

export default function CreditsPage() {
  const { organization, loading: creditsLoading } = useOrganization();
  const [amountEur, setAmountEur] = useState<number>(50);
  const [creditsPerEuro, setCreditsPerEuro] = useState<number>(DEFAULT_CREDITS_PER_EUR);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const credits = organization?.credits || 0;
  const clampedAmount = useMemo(
    () => Math.min(MAX_EUR, Math.max(MIN_EUR, Number.isFinite(amountEur) ? amountEur : MIN_EUR)),
    [amountEur]
  );
  const currencyFormatter = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }), []);
  const creditsFormatter = useMemo(() => new Intl.NumberFormat('en-US'), []);
  const mintedCredits = useMemo(() => clampedAmount * creditsPerEuro, [clampedAmount, creditsPerEuro]);

  useEffect(() => {
    if (amountEur !== clampedAmount) setAmountEur(clampedAmount);
  }, [amountEur, clampedAmount]);

  useEffect(() => {
    let isMounted = true;
    const loadPricingSettings = async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/datapoint-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const value = data?.settings?.eur_to_credits;
        if (isMounted && typeof value === 'number' && value > 0) {
          setCreditsPerEuro(value);
        }
      } catch (error) {
        toast.error(handleApiError(error));
      }
    };
    loadPricingSettings();
    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently]);

  if (!organization) return null;

  const handleCheckout = async () => {
    if (clampedAmount < MIN_EUR || clampedAmount > MAX_EUR) {
      toast.error(`Amount must be between €${MIN_EUR.toLocaleString()} and €${MAX_EUR.toLocaleString()}.`);
      return;
    }
    trackCreditsCheckoutStart({ amount_eur: clampedAmount });
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/stripe/create-credit-checkout-session`,
        { quantity: clampedAmount },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (data.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('Unexpected response from checkout. Please try again.');
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const QuickPick = ({ value }: { value: number }) => (
    <button
      type="button"
      onClick={() => {
        trackCreditsQuickPick({ amount_eur: value });
        setAmountEur(value);
      }}
      className={`rounded-full px-3 py-1 text-sm ring-1 transition ${
        amountEur === value
          ? 'bg-blue-600 text-white ring-blue-600'
          : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50'
      }`}
    >
      €{value.toLocaleString()} ({creditsFormatter.format(value * creditsPerEuro)} cr)
    </button>
  );

  const closePopup = () => {
    trackCreditsModalClose({ amount_eur: clampedAmount });
    setShowPopup(false);
  };

  return (
    <StandardPage title="Credits" marginBottom={40}>
      <div className="max-w-4xl rounded-2xl bg-gradient-to-br from-white to-slate-50 p-8 shadow-xl ring-1 ring-slate-100">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900">Your Balance</h2>
            <p className="mt-1 text-sm text-slate-500">
              Pay-as-you-go: 1 EUR = {creditsFormatter.format(creditsPerEuro)} credits.
              Credits are shared between the data APIs and satellite imagery.
            </p>
          </div>
          {creditsLoading ? (
            <p className="animate-pulse text-lg text-slate-500">Loading…</p>
          ) : (
            <div className="rounded-xl bg-emerald-50 px-4 py-2 text-2xl font-extrabold text-emerald-700 ring-1 ring-emerald-200">
              {credits.toLocaleString()} Credits
            </div>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <div className="order-2 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:order-1 md:col-span-2">
            <h3 className="mb-3 text-lg font-semibold text-slate-900">Top up credits</h3>

            <div className="mb-4 flex flex-wrap gap-2">
              {[5, 10, 25, 50, 100, 250, 500, 1000].map((v) => (
                <QuickPick key={v} value={v} />
              ))}
            </div>

            <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
              <label htmlFor="amount-eur" className="text-sm font-medium text-slate-700">
                Purchase amount (EUR)
              </label>
              <div className="justify-self-end text-xs text-slate-500">
                Min €{MIN_EUR.toLocaleString()} • Max €{MAX_EUR.toLocaleString()}
              </div>

              <input
                id="amount-eur"
                type="number"
                min={MIN_EUR}
                max={MAX_EUR}
                step={STEP}
                value={amountEur}
                onChange={(e) => {
                  const value = Math.min(MAX_EUR, Math.max(MIN_EUR, Number(e.target.value)));
                  setAmountEur(value);
                }}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="hidden sm:block"></div>

              <input
                type="range"
                min={MIN_EUR}
                max={MAX_EUR}
                step={STEP}
                value={amountEur}
                onChange={(e) => setAmountEur(Number(e.target.value))}
                className="col-span-2 mt-2 w-full accent-blue-600"
              />
            </div>
          </div>

          <div className="order-1 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:order-2">
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Order summary</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-600">Credits</dt>
                <dd className="font-medium text-slate-900">{creditsFormatter.format(mintedCredits)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-600">Conversion</dt>
                <dd className="font-medium text-slate-900">1 EUR = {creditsFormatter.format(creditsPerEuro)} cr</dd>
              </div>
              <div className="my-2 h-px bg-slate-200" />
              <div className="flex items-baseline justify-between">
                <dt className="text-slate-600">Total</dt>
                <dd className="text-xl font-extrabold text-slate-900">{currencyFormatter.format(clampedAmount)}</dd>
              </div>
            </dl>

            <button
              onClick={() => {
                trackCreditsAddClick({ amount_eur: clampedAmount });
                setShowPopup(true);
              }}
              className="mt-6 w-full rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 font-semibold text-white shadow-lg ring-1 ring-blue-500/20 transition hover:scale-[1.01] hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Credits
            </button>
          </div>
        </div>

        <div className="mt-6 space-y-2 rounded-xl bg-slate-50 p-4 text-xs text-slate-600 ring-1 ring-slate-200">
          <p className="font-semibold text-slate-700">How credits are used:</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Satellite imagery: credits are deducted based on the final image cost per task.</li>
            <li>
              Data APIs (conflict, population, funding, infrastructure): credits are deducted per datapoint returned.
              The default rate is 1 credit per point and can be adjusted by the CivAPI team.
            </li>
            <li>
              ACLED conflict events: 1 credit per datapoint for licensed countries and 1,000 credits per datapoint for
              unlicensed access.
            </li>
          </ul>
          <p>Payments are processed with Stripe and credits become available immediately after checkout.</p>
        </div>
      </div>

      <Transition show={showPopup} as={Fragment}>
        <Dialog initialFocus={closeBtnRef} onClose={closePopup} className="relative z-[9999]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100 transition-all">
                  <Dialog.Title className="mb-1 text-lg font-semibold text-slate-900">Checkout</Dialog.Title>
                  <p className="mb-4 text-sm text-slate-600">Confirm your purchase amount and continue to Stripe.</p>

                  <div className="mb-4 grid gap-3">
                    <label htmlFor="amount-modal" className="text-sm font-medium text-slate-700">
                      Purchase amount (EUR)
                    </label>
                    <input
                      id="amount-modal"
                      type="number"
                      min={MIN_EUR}
                      max={MAX_EUR}
                      step={STEP}
                      value={amountEur}
                      onChange={(e) => {
                        const value = Math.min(MAX_EUR, Math.max(MIN_EUR, Number(e.target.value)));
                        setAmountEur(value);
                      }}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="range"
                      min={MIN_EUR}
                      max={MAX_EUR}
                      step={STEP}
                      value={amountEur}
                      onChange={(e) => setAmountEur(Number(e.target.value))}
                      className="w-full accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Min €{MIN_EUR.toLocaleString()}</span>
                      <span>Max €{MAX_EUR.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="mb-5 rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-slate-600">Total</span>
                      <span className="text-xl font-extrabold text-slate-900">
                        {currencyFormatter.format(clampedAmount)}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      You will receive approximately {creditsFormatter.format(mintedCredits)} credits.
                    </p>
                  </div>

                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      ref={closeBtnRef}
                      onClick={closePopup}
                      className="rounded-full bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCheckout}
                      disabled={loading || clampedAmount < MIN_EUR || clampedAmount > MAX_EUR}
                      className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50"
                    >
                      {loading ? 'Redirecting…' : 'Continue'}
                    </button>
                  </div>

                  <p className="mt-3 text-[11px] text-slate-500">
                    By continuing you agree to our Terms of Use and Privacy Policy.
                  </p>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </StandardPage>
  );
}
